/**
 * Backend API Client
 * 
 * Centralized HTTP client for all frontend-to-backend communication.
 * Features:
 * - 3-second default timeout (configurable per-request)
 * - Graceful teardown (cancel all in-flight requests)
 * - Automatic auth token injection
 * - Consistent error handling
 */

import { Capacitor, CapacitorHttp } from '@capacitor/core';

const DEFAULT_API_BASE_URL = 'http://localhost:5000';
const NATIVE_FALLBACK_API_BASE_URL = 'https://api.9meals.ai';

function resolveApiBaseUrl() {
  const configuredUrl =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    DEFAULT_API_BASE_URL;

  // On native devices, localhost points to the phone itself (not your backend).
  // If env points to localhost, fall back to production API.
  const isNative = (() => {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  })();

  if (!isNative) {
    return configuredUrl;
  }

  const isLocalhostTarget = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(configuredUrl);
  return isLocalhostTarget ? NATIVE_FALLBACK_API_BASE_URL : configuredUrl;
}

const API_BASE_URL = resolveApiBaseUrl();

// Custom error classes for better error handling
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network error') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class RequestCancelledError extends Error {
  constructor(message = 'Request was cancelled') {
    super(message);
    this.name = 'RequestCancelledError';
  }
}

class BackendClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.defaultTimeout = 3000; // 3 seconds
    this.activeRequests = new Map(); // Track active requests for graceful teardown
    this.requestIdCounter = 0;
  }

  isNativeRequest(url) {
    try {
      return Capacitor.isNativePlatform() && /^https?:\/\//i.test(url);
    } catch {
      return false;
    }
  }

  /**
   * Get the auth token from localStorage
   * @returns {string|null}
   */
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Build headers for the request
   * @param {Object} customHeaders - Additional headers to include
   * @returns {Object}
   */
  buildHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Create an AbortController with timeout
   * @param {number} timeout - Timeout in milliseconds
   * @returns {{ controller: AbortController, timeoutId: number }}
   */
  createAbortController(timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort('timeout');
    }, timeout);

    return { controller, timeoutId };
  }

  /**
   * Make an HTTP request
   * @param {string} endpoint - API endpoint (e.g., '/api/mealplan')
   * @param {Object} options - Request options
   * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
   * @param {Object} options.body - Request body (will be JSON stringified)
   * @param {Object} options.headers - Additional headers
   * @param {number} options.timeout - Request timeout in ms (default: 3000)
   * @param {boolean} options.includeCredentials - Include cookies (default: true)
   * @returns {Promise<Object>} - Response data
   */
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body,
      headers: customHeaders = {},
      timeout = this.defaultTimeout,
      includeCredentials = true,
    } = options;

    const requestId = ++this.requestIdCounter;
    const { controller, timeoutId } = this.createAbortController(timeout);

    // Track this request
    this.activeRequests.set(requestId, { controller, timeoutId, endpoint });

    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.buildHeaders(customHeaders);
    const fetchOptions = {
      method,
      headers,
      signal: controller.signal,
    };

    if (includeCredentials) {
      fetchOptions.credentials = 'include';
    }

    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      let response;

      if (this.isNativeRequest(url)) {
        const nativeResponse = await CapacitorHttp.request({
          url,
          method,
          headers,
          data: body,
          connectTimeout: timeout,
          readTimeout: timeout,
          responseType: 'json',
        });

        response = {
          ok: nativeResponse.status >= 200 && nativeResponse.status < 300,
          status: nativeResponse.status,
          json: async () => nativeResponse.data,
        };
      } else {
        response = await fetch(url, fetchOptions);
      }

      // Clear timeout since request completed
      clearTimeout(timeoutId);

      // Handle 401 - token definitely expired or invalid
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        const error = new Error('Authentication failed');
        error.status = response.status;
        error.isAuthError = true;
        throw error;
      }

      // Handle 429 - Too Many Requests (rate limiting, no tries remaining)
      // This is NOT an auth error - don't remove the token
      if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Too many requests');
        error.status = 429;
        error.data = errorData;
        error.isRateLimitError = true;
        throw error;
      }

      // Handle 403 - check if it's an auth error or a business logic error
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Forbidden');
        error.status = 403;
        error.data = errorData;
        
        // Only treat as auth error if it's NOT a "no tries remaining" or similar business logic error
        const isBusinessLogicError = errorData.code === 'NO_TRIES_REMAINING' ||
          errorData.error?.toLowerCase().includes('tries') ||
          errorData.error?.toLowerCase().includes('limit');
        
        if (!isBusinessLogicError) {
          localStorage.removeItem('authToken');
          error.isAuthError = true;
        }
        throw error;
      }

      // Handle non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      // Parse JSON response
      const data = await response.json();
      return data;

    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error.name === 'AbortError') {
        if (controller.signal.reason === 'timeout') {
          throw new TimeoutError(`Request to ${endpoint} timed out after ${timeout}ms`);
        }
        throw new RequestCancelledError(`Request to ${endpoint} was cancelled`);
      }

      // Handle network errors
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new NetworkError(`Network error while requesting ${endpoint}`);
      }

      if (this.isNativeRequest(url) && !error.status) {
        throw new NetworkError(`Network error while requesting ${endpoint}`);
      }

      // Re-throw other errors
      throw error;

    } finally {
      // Remove from active requests
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options (timeout, headers)
   * @returns {Promise<Object>}
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options (timeout, headers)
   * @returns {Promise<Object>}
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options (timeout, headers)
   * @returns {Promise<Object>}
   */
  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options (timeout, headers)
   * @returns {Promise<Object>}
   */
  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options (timeout, headers)
   * @returns {Promise<Object>}
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Cancel all active requests (graceful teardown)
   * Call this on logout or app unmount
   */
  cancelAllRequests() {
    for (const { controller, timeoutId } of this.activeRequests.values()) {
      clearTimeout(timeoutId);
      controller.abort('cancelled');
    }
    this.activeRequests.clear();
    console.log('🔌 All pending requests cancelled');
  }

  /**
   * Get count of active requests
   * @returns {number}
   */
  getActiveRequestCount() {
    return this.activeRequests.size;
  }

  /**
   * Set the default timeout for all requests
   * @param {number} timeout - Timeout in milliseconds
   */
  setDefaultTimeout(timeout) {
    this.defaultTimeout = timeout;
  }
}

// Export singleton instance
const backendClient = new BackendClient();
export default backendClient;

// Export base URL for OAuth redirects (can't use fetch for OAuth)
export { API_BASE_URL };
